import type { ReactNode } from "react";

export type ResponsiveRecordColumn = {
  key: string;
  header: ReactNode;
  mobileLabel: string;
  className?: string;
};

export type ResponsiveRecordRow = {
  key: string;
  cells: Record<string, ReactNode>;
};

export type ResponsiveRecordTableProps = {
  columns: ResponsiveRecordColumn[];
  rows: ResponsiveRecordRow[];
  tableClassName?: string;
  ariaLabel?: string;
};

/**
 * A semantic table for wider layouts and a complete label/value record list
 * for phones. Both renderings use the same supplied cell values, so links,
 * badges, controls and source details stay available at either width.
 */
export function ResponsiveRecordTable({
  columns,
  rows,
  tableClassName,
  ariaLabel,
}: ResponsiveRecordTableProps) {
  if (rows.length === 0) return null;

  const tableClasses = ["node-list-table", tableClassName].filter(Boolean).join(" ");

  return (
    <div className="responsive-records">
      <div className="responsive-records-table">
        <table className={tableClasses} aria-label={ariaLabel}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} scope="col" className={column.className}>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                {columns.map((column) => (
                  <td key={column.key} className={column.className}>
                    {row.cells[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="responsive-records-cards" role="list" aria-label={ariaLabel}>
        {rows.map((row) => (
          <article className="responsive-record-card" role="listitem" key={row.key}>
            <dl>
              {columns.map((column) => (
                <div className="responsive-record-field" key={column.key}>
                  <dt className="responsive-record-label">{column.mobileLabel}</dt>
                  <dd className="responsive-record-value">{row.cells[column.key]}</dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>
    </div>
  );
}
